import Drill, { HashType } from "@drillflo/drillcore/lib/Drill";
import Collaboration, {
  HistoryEntry,
  User,
} from "@drillflo/drillcore/lib/Collaboration";
import { useState, useEffect, useCallback } from "react";
import Engine from "@drillflo/drillcore/lib/2d/Engine";
import { Auth } from "firebase/auth";
import { useUser } from "./useUser.tsx";
import { useDrillViewData } from "./useFieldEditorShared.tsx";

const collaborationUri =
  process.env.REACT_APP_DRILLFLO_COLLABORATION_WEBSOCKET_URL ??
  "wss://collaboration.drillflo.com";

export interface CollaborationReturn {
  collaboration: Collaboration | null;
  canUndo: boolean;
  canRedo: boolean;
  collaborationHistoryLoading: boolean;
  drillModifiedByCollaboration: number;
  forceStateUpdate: () => void;
  currentDrillVersion: string | null;
  getVersionHistory: (callback: (history: HistoryEntry[]) => void) => void;
  otherCollaborators: User[];
  collaborationDisabled: boolean;
}

export const useCollaboration = (
  auth: Auth | null,
  drill: Drill | null,
  drillId: string | null,
  engine: Engine | null
) => {
  const [collaboration, setCollaboration] = useState<Collaboration | null>(
    null
  );
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [collaborationHistoryLoading, setCollaborationHistoryLoading] =
    useState(true);
  const [currentDrillVersion, setCurrentDrillVersion] = useState<string | null>(
    null
  );
  const [otherCollaborators, setOtherCollaborators] = useState<User[]>([]);

  // If collaboration is disabled because user is not premium or pro, and
  // the drill is currently being edited by one or more other users.
  const [collaborationDisabled, setCollaborationDisabled] = useState(false);

  const [drillModifiedByCollaboration, setDrillModifiedByCollaboration] =
    useState(0);

  const { userData } = useUser(auth);

  useEffect(() => {
    if (drill && drill.engine) {
      const onDrillUpdatedFromCollaboration = () => {
        setDrillModifiedByCollaboration((count) => count + 1);
      };

      drill.engine.on(
        "drill-updated-from-collaboration",
        onDrillUpdatedFromCollaboration
      );

      return () => {
        if (drill && drill.engine) {
          drill.engine.removeOn(
            "drill-updated-from-collaboration",
            onDrillUpdatedFromCollaboration
          );
        }
      };
    }
  }, [drill]);

  useEffect(() => {
    async function setupCollaboration() {
      if (auth && auth.currentUser && drill && drillId && engine && userData) {
        // Collaboration binds the drill and engine once in its constructor and has no
        // way to rebind them. forceViewRecreate replaces both objects, so matching on
        // drillId alone would keep a Collaboration pointing at the previous drill: undo
        // and redo would still be applied, but to an instance nothing renders, and local
        // edits would stop syncing because _handleDrillModified bails when the drill it
        // receives is not the one it holds.
        if (
          collaboration &&
          collaboration.drillId === drillId &&
          collaboration.drill === drill &&
          collaboration.engine === engine
        ) {
          return;
        }

        const userId = auth.currentUser.uid;
        const userToken = await auth.currentUser.getIdToken();

        const collab = new Collaboration(
          collaborationUri,
          engine,
          drill,
          drillId,
          userId,
          userData.firstName,
          userData.lastName,
          userToken
        );
        setCollaboration((c) => {
          if (c) {
            c.disconnect();
            setCollaboration(null);
          }
          collab.connect();

          return collab;
        });
      }
    }
    setupCollaboration();

    return () => {
      if (collaboration) {
        collaboration.disconnect();
        setCollaboration(null);
      }
    };
  }, [auth, drill, drillId, engine, collaboration, userData]);

  const forceStateUpdate = useCallback(() => {
    if (collaboration && collaboration.isConnected && !(window as any).$playbackState.isPlaying) {
      collaboration.getUndoRedoState((state) => {
        setCanUndo(state.canUndo);
        setCanRedo(state.canRedo);
        setCollaborationHistoryLoading(false);
        setCurrentDrillVersion(state.currentVersionId);
      });

      if (auth && auth.currentUser) {
        // const uid = auth.currentUser.uid;
        setOtherCollaborators(
          []
          // Commented out until this is fixed
          // collaboration.getUsers().filter((u) => u.userId !== uid)
        );
      }
    }
  }, [collaboration, auth]);

  const getVersionHistory = useCallback(
    (callback: (history: HistoryEntry[]) => void) => {
      if (collaboration && collaboration.isConnected) {
        collaboration.getHistory(callback);
      }
    },
    [collaboration]
  );

  useEffect(() => {
    if (collaboration) {
      const timer = setInterval(() => {
        forceStateUpdate();
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [collaboration, forceStateUpdate]);

  useEffect(() => {
    if (otherCollaborators.length > 0 && userData) {
      if (userData.plan === "base" && otherCollaborators.length > 0) {
        setCollaborationDisabled(true);
      } else {
        setCollaborationDisabled(false);
      }
    } else {
      setCollaborationDisabled(false);
    }
  }, [otherCollaborators, userData]);

  const ret: CollaborationReturn = {
    collaboration,
    canUndo,
    canRedo,
    collaborationHistoryLoading,
    drillModifiedByCollaboration,
    forceStateUpdate,
    currentDrillVersion,
    getVersionHistory,
    otherCollaborators,
    collaborationDisabled,
  };

  return ret;
};
